/**
 * Sound tuning for everything that is not a weapon.
 *
 * Data only. The weapon's own report lives beside its rate of fire and dispersion in
 * `weapons.ts`, because it is part of that weapon's feel; these are the rig's noises, and
 * they belong to the rig.
 */

/**
 * The rig's horn.
 *
 * Sounded manually on H, and automatically when a vessel makes the safety zone. It was a
 * synthesised two-tone before, which read as a game chime rather than as several tonnes of
 * air horn on a steel platform.
 */
export const HORN_SOUND = {
  url: '/audio/horn.wav',
  /**
   * Loud, because a horn is. It is also rare — a handful of times a shift — so unlike the
   * gun it never has to leave headroom for fifteen copies of itself.
   */
  gain: 0.85,
  /**
   * Everything else ducks under it while it sounds, and recovers over the tail.
   *
   * A horn that merely adds to the mix reads as a sample being played. A horn that pushes
   * the sea, the wind and the gun down for its duration reads as something enormous going
   * off next to you, which is what it is.
   */
  duckOthers: 0.42,
  duckAttack: 0.05,
  duckRelease: 1.4,
} as const;

/**
 * The radar's acquisition tone, sounded when a new contact appears on the scope.
 *
 * Two takes, chosen by whether the contact squawks IFF. This is the one place the game
 * tells the player something about a vessel before they can see it, and it is not a
 * contradiction of the detection ladder but the reason the ladder has a bottom rung: a
 * transponder answers, or it does not. What the radar still cannot tell you is *what* the
 * thing is — a silent contact could be a fishing boat — only that nothing aboard it
 * identified itself.
 */
export const CONTACT_PING = {
  friendlyUrl: '/audio/friendly-ping.wav',
  hostileUrl: '/audio/enemy-ping.wav',
  /**
   * Well under the horn. This is a console tone a metre from the player's ear, not an
   * event out on the water, so it neither ducks anything nor takes a distance delay.
   */
  gain: 0.55,
  /**
   * Contacts arrive in packs — three skiffs spawn on the same tick — and three identical
   * tones stacked one frame apart read as a glitch rather than as three boats. Inside
   * this window a second contact of the same allegiance is folded into the first ping.
   */
  coalesceWindow: 0.45,
} as const;

/**
 * Distant gunfire, played while hostiles are actually working over a friendly hull.
 *
 * The convoy is the thing the player is paid to protect, and until now an attack on it
 * three kilometres out was silent — the tanker simply lost integrity while the sea made
 * ocean noises. This is the cue that says look over there, and it is deliberately a bed
 * rather than an event: it fades up while the engagement exists and fades out when the
 * last attacker breaks off or dies.
 *
 * No speed-of-sound delay, unlike every impact in the game. A continuous bed has no
 * onset to delay, and shifting a loop by a few seconds would change nothing a player
 * could perceive except when it stops.
 */
export const BATTLE_AMBIENCE = {
  url: '/audio/faraway-battle.wav',
  /** Level at the reference distance, before falloff. */
  gain: 0.55,
  /**
   * Long, because this is the one sound that is supposed to carry. Engagements on the
   * convoy happen between one and three kilometres out; at the short reference the
   * impacts use it would be inaudible exactly where it matters.
   */
  referenceRange: 1800,
  /** Seconds to fade in when an engagement starts, and out when it ends. */
  fadeIn: 1.6,
  fadeOut: 2.2,
} as const;

/**
 * Incoming fire striking the platform — bullets hitting nearby steel and ricocheting.
 *
 * Adam's recordings, five takes, played per `RigHit` whose cause is gunfire. This is the
 * sound of being personally under attack: the rig-mission hostiles close and shoot at
 * the structure the player is standing on, and until these, every hit was one
 * synthesised structural clang. The creature keeps that clang — a tentacle on the legs
 * does not ricochet.
 */
export const RICOCHET_SOUND = {
  urls: [
    '/audio/bullet-hit-1.wav',
    '/audio/bullet-hit-2.wav',
    '/audio/bullet-hit-3.wav',
    '/audio/bullet-hit-4.wav',
    '/audio/bullet-hit-5.wav',
  ],
  /**
   * Present but not dominating: under sustained attack this plays every couple of
   * seconds, and it must never bury the gun, the radio or the alarm that is telling
   * the player the same story.
   */
  gain: 0.6,
  /** In sustained fire the newest impacts matter; older tails get shed past this. */
  maxVoices: 4,
  /** Small pitch spread so five takes do not read as five loops. */
  pitchJitter: 0.06,
  /**
   * Random onset scatter, seconds. The splash event lands on the exact sim tick; a
   * ricochet snapping at frame-perfect times reads as a sound effect, and two rounds
   * arriving on the same tick would stack into one doubled hit.
   */
  scatter: 0.12,
} as const;

/**
 * The creature crushing the platform — steel giving way under something enormous.
 *
 * Adam's recordings, four takes, played on creature-cause `RigHit`s: the tentacle
 * grapples of the finale, which are the Kraken climbing the rig. His rule, kept here as
 * data: never two at once, and clear air between them — six limbs grapple on their own
 * cycles, and without the floor the takes would gatling into mud. A grapple that lands
 * while one take is still sounding is covered by it: the steel is already groaning.
 */
export const KRAKEN_CRUSH_SOUND = {
  urls: [
    '/audio/kraken-crush-rig-1.wav',
    '/audio/kraken-crush-rig-2.wav',
    '/audio/kraken-crush-rig-3.wav',
    '/audio/kraken-crush-rig-4.wav',
  ],
  /** Loud — the finale's own percussion — but still under the horn and the voice. */
  gain: 0.7,
  /** Seconds of clear air after a take ends before the next may start. */
  minGap: 1.5,
  /** Slight spread so four takes carry a whole boss fight without reading as a loop. */
  pitchJitter: 0.05,
} as const;

/**
 * The finale's own voice — Adam's recordings for the four beats that end the campaign.
 *
 * Every one of these is keyed to a phase of `sim/kraken.ts` rather than to a position or
 * a distance, because by this point the creature IS the world: it is a hundred metres
 * off the platform and there is nothing else in the water. Distance attenuation would
 * only make the loudest moment of the game quieter.
 */
export const KRAKEN_FINALE_SOUND = {
  /**
   * The limbs on the structure, looped for as long as the assault lasts.
   *
   * Adam's level: half. It is a bed under the fight, not the fight — the gun, the crush
   * takes and the radio all have to cut through it.
   */
  climb: {
    url: '/audio/kraken-climbing-loop.wav',
    gain: 0.5,
    fadeIn: 1.2,
    fadeOut: 1.8,
  },
  /** The head coming up out of the dark, once, on the rise. */
  headReveal: { url: '/audio/kraken-head-reveal.wav', gain: 0.95 },
  /**
   * The stare, alternating two takes back to back for as long as the eye is on the
   * player.
   *
   * Alternating rather than looping one file: this is the stretch where the player is
   * expected to hesitate, and a single loop would become a texture within two passes.
   * Each take is chained on the previous one's end, so the join is seamless and the
   * pair reads as one continuous held breath.
   */
  eyeContact: {
    urls: ['/audio/kraken-eye-contact.wav', '/audio/kraken-eye-contact-2.wav'],
    gain: 0.9,
    fadeOut: 0.5,
  },
  /** The eye rolling back and the mass beginning to go over. */
  dying: { url: '/audio/kraken-dying.wav', gain: 1 },
  /** Six hundred metres of animal meeting the sea. The loudest thing in the game. */
  impact: { url: '/audio/kraken-hitting-ocean.wav', gain: 1.1 },
} as const;

/**
 * The comms channel: how the voice sits in the mix.
 *
 * The takes themselves are recorded clean and treated here, so the radio character can be
 * retuned without re-recording sixty-three lines.
 */
export const COMMS = {
  /** Band-pass centre and width — a communications channel, roughly 300–3,400 Hz. */
  centreHz: 1400,
  q: 0.75,
  /** A little tanh drive: the sound of a signal working slightly too hard. */
  drive: 1.8,
  gain: 1.15,
  clickGain: 0.5,
  /** How far the rest of the mix drops under a transmission. */
  duckOthers: 0.35,
  /**
   * How long a queued line may wait before it is dropped rather than spoken late.
   *
   * Speech takes seconds and text does not, so a dense stretch can leave the channel
   * behind the fight. A voice reporting a contact that died fifteen seconds ago is worse
   * than silence — and the line's text was already on screen when it mattered.
   */
  staleSeconds: 8,
} as const;
